
import { useMemo } from "react";
import { cn } from "@/lib/utils";
import { EventType } from "@/types/event";
import { useToast } from "@/hooks/use-toast";
import { PlusCircle } from "lucide-react";

interface MonthViewProps {
  date: Date;
  events: EventType[];
  onSelectDate: (date: Date) => void;
  onCreateEvent?: (date: Date) => void;
  isDemoMode?: boolean;
}

const MonthView = ({ date, events, onSelectDate, onCreateEvent, isDemoMode = false }: MonthViewProps) => {
  const { toast } = useToast();
  
  // Generate calendar grid
  const calendar = useMemo(() => {
    const year = date.getFullYear();
    const month = date.getMonth();
    
    // First day of month
    const firstDay = new Date(year, month, 1);
    // Last day of month
    const lastDay = new Date(year, month + 1, 0);
    
    // Creating calendar grid (6 weeks max)
    const grid = [];
    let currentDate = new Date(firstDay);
    
    // Adjust to start from Sunday (or the first day of the week)
    currentDate.setDate(firstDay.getDate() - firstDay.getDay());
    
    // Create 6 weeks (42 days) to ensure we have enough rows
    for (let week = 0; week < 6; week++) {
      const weekDays = [];
      
      for (let day = 0; day < 7; day++) {
        const clonedDate = new Date(currentDate);
        
        // Check if date is in the current month
        const isCurrentMonth = currentDate.getMonth() === month;
        
        // Check if date is today
        const isToday = currentDate.toDateString() === new Date().toDateString();
        
        // Get events for this date
        const dayEvents = events.filter(event => {
          const eventDate = new Date(event.startDate);
          return eventDate.toDateString() === currentDate.toDateString();
        });
        
        // Flag to indicate if this date has events
        const hasEvents = dayEvents.length > 0;
        
        weekDays.push({
          date: clonedDate,
          isCurrentMonth,
          isToday,
          events: dayEvents,
          hasEvents
        });
        
        // Move to next day
        currentDate.setDate(currentDate.getDate() + 1);
      }
      
      grid.push(weekDays);
      
      // If we've already included the last day of the month and completed the week, break
      if (currentDate > lastDay && currentDate.getDay() === 0) {
        break;
      }
    }
    
    return grid;
  }, [date, events]);

  // Day names
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  
  // Handle date selection 
  const handleDateSelect = (day: any) => {
    if (day.hasEvents) {
      onSelectDate(day.date);
    } else {
      // Show tooltip that there are no events, but don't prevent selection
      toast({
        title: "No events on this date",
        description: "There are no scheduled events for this date.",
        variant: "default",
      });
      onSelectDate(day.date);
    }
  };

  // Handle creating a new event
  const handleCreateEvent = (day: any, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering the date selection
    
    if (isDemoMode) {
      toast({
        title: "Demo Mode",
        description: "This is a demonstration calendar. Sign up for a full account to create events.",
        variant: "default",
      });
      return;
    }
    
    if (onCreateEvent) {
      // Set the time to 9 AM for a reasonable default
      const eventDate = new Date(day.date);
      eventDate.setHours(9, 0, 0, 0);
      onCreateEvent(eventDate);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Calendar header with day names */}
      <div className="grid grid-cols-7 mb-2">
        {dayNames.map(day => (
          <div 
            key={day} 
            className="text-sm font-medium text-gray-500 text-center py-2"
          >
            {day}
          </div>
        ))}
      </div>
      
      {/* Calendar grid */}
      <div className="flex-1 grid grid-cols-7 grid-rows-6 gap-1">
        {calendar.flat().map((day, index) => (
          <div
            key={index}
            onClick={() => handleDateSelect(day)}
            className={cn(
              "min-h-[100px] p-1 border rounded transition-colors relative group",
              day.isCurrentMonth ? "bg-white" : "bg-gray-50 text-gray-400",
              day.isToday && "border-blue-400",
              !day.isCurrentMonth && "opacity-50",
              "cursor-pointer hover:bg-gray-50"
            )}
          >
            <div className="flex justify-between items-start">
              <span className={cn(
                "text-sm font-medium inline-flex h-6 w-6 items-center justify-center rounded-full",
                day.isToday ? "bg-blue-100 text-blue-700" : ""
              )}>
                {day.date.getDate()}
              </span>
              {/* Add create event button that shows on hover */}
              {!isDemoMode && day.isCurrentMonth && (
                <button 
                  className="h-6 w-6 rounded-full bg-blue-100 text-blue-600 hover:bg-blue-200 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={(e) => handleCreateEvent(day, e)}
                  title="Create new event"
                >
                  <PlusCircle className="h-4 w-4 mx-auto" />
                </button>
              )}
              {day.hasEvents && (
                <span className="h-2 w-2 rounded-full bg-blue-500"></span>
              )}
            </div>
            
            {/* Events list */}
            <div className="mt-1 space-y-1 overflow-y-auto max-h-[80px]">
              {day.events.map((event, i) => (
                <div
                  key={i}
                  className={`text-xs truncate rounded px-1 py-0.5 ${event.color}`}
                >
                  {event.title}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MonthView;
